// Height Filter Range Grouping
// Groups individual height values into ranges with range-level selection

class HeightFilterGrouping {
  constructor() {
    // Define height ranges (in inches)
    this.ranges = [
      { min: 0, max: 12, label: 'Under 12"' },
      { min: 12, max: 24, label: '12" - 24"' },
      { min: 24, max: 36, label: '24" - 36"' },
      { min: 36, max: 48, label: '36" - 48"' },
      { min: 48, max: 60, label: '48" - 60"' },
      { min: 60, max: 72, label: '60" - 72"' },
      { min: 72, max: Infinity, label: 'Over 72"' }
    ];
    
    this.init();
  }

  init() {
    // Wait for DOM content to load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.groupHeightFilters();
        this.fixAllHeightLabels();
      });
    } else {
      this.groupHeightFilters();
      this.fixAllHeightLabels();
    }

    // Re-apply grouping after facets are updated via AJAX
    this.observeFacetUpdates();
  }

  groupHeightFilters() {
    // Find the height filter container
    const heightFilter = document.querySelector('[id*="filter.p.m.custom.height_text"]');
    if (!heightFilter) return;

    const filterContainer = heightFilter.closest('details');
    if (!filterContainer) return;
    
    // Fix the label text in the summary button
    const summaryButton = filterContainer.querySelector('summary .facets__label');
    if (summaryButton) {
      const labelText = summaryButton.textContent;
      if (labelText.includes('Height_Text')) {
        summaryButton.innerHTML = summaryButton.innerHTML.replace(/Height_Text/g, 'Height');
      }
    }
    
    // Fix the aria-label on the summary element
    const summary = filterContainer.querySelector('summary[aria-label*="Height_Text"]');
    if (summary) {
      const ariaLabel = summary.getAttribute('aria-label');
      if (ariaLabel) {
        summary.setAttribute('aria-label', ariaLabel.replace(/Height_Text/g, 'Height'));
      }
    }

    const filterList = filterContainer.querySelector('.facets__list, .facets__list--vertical');
    if (!filterList) return;

    // Get all height filter items
    const filterItems = Array.from(filterList.querySelectorAll('li.facets__item'));
    if (filterItems.length === 0) return;

    // Parse height values and group them
    const groupedHeights = this.groupHeightsByRange(filterItems);
    
    // Create new grouped filter HTML
    const newFilterHTML = this.createGroupedFilterHTML(groupedHeights, filterContainer);
    
    // Replace the existing filter list
    filterList.innerHTML = newFilterHTML;
    
    // Add event listeners to the new checkboxes
    this.attachEventListeners(filterList);
  }

  groupHeightsByRange(filterItems) {
    const groups = {};
    
    // Initialize groups
    this.ranges.forEach(range => {
      groups[range.label] = {
        range: range,
        items: [],
        totalCount: 0,
        values: []
      };
    });

    filterItems.forEach(item => {
      const checkbox = item.querySelector('input[type="checkbox"]');
      const label = item.querySelector('.facet-checkbox__text-label');
      
      if (!checkbox || !label) return;
      
      // Extract height value (remove "inches" text)
      const heightText = label.textContent.trim().replace(' inches', '');
      const heightValue = parseFloat(heightText);
      
      if (isNaN(heightValue)) return;
      
      // Find the appropriate range
      for (const range of this.ranges) {
        if (heightValue >= range.min && heightValue < range.max) {
          // Get product count
          const countMatch = item.textContent.match(/\((\d+)\)/);
          const count = countMatch ? parseInt(countMatch[1]) : 0;
          
          groups[range.label].items.push({
            value: checkbox.value,
            paramName: checkbox.name,
            label: heightText,
            count: count,
            checked: checkbox.checked,
            disabled: checkbox.disabled,
            originalElement: item
          });
          
          groups[range.label].totalCount += count;
          groups[range.label].values.push(checkbox.value);
          break;
        }
      }
    });

    // Filter out empty groups
    return Object.entries(groups).filter(([_, group]) => group.items.length > 0);
  }

  createGroupedFilterHTML(groupedHeights, filterContainer) {
    let html = '';
    const isVertical = filterContainer.querySelector('.facets__list--vertical') !== null;
    const showMoreNumber = 10;
    let itemIndex = 0;

    groupedHeights.forEach(([rangeLabel, group], groupIndex) => {
      const groupId = `height-range-${groupIndex}`;
      const rangeCheckboxId = `height-range-checkbox-${groupIndex}`;
      
      // Check if all items in the group are selected
      const allChecked = group.items.every(item => item.checked);
      const someChecked = group.items.some(item => item.checked);
      const isDisabled = group.items.every(item => item.disabled);
      
      // Create the range group item with a checkbox for the entire range
      html += `
        <li class="list-menu__item facets__item height-range-group${itemIndex >= showMoreNumber && isVertical ? ' show-more-item hidden' : ''}">
          <label for="${rangeCheckboxId}" class="facets__label facet-checkbox height-range-checkbox${isDisabled ? ' disabled' : ''}${allChecked ? ' active' : ''}">
            <input
              type="checkbox"
              id="${rangeCheckboxId}"
              class="height-range-master-checkbox"
              data-group-index="${groupIndex}"
              ${allChecked ? 'checked' : ''}
              ${someChecked && !allChecked ? 'indeterminate' : ''}
              ${isDisabled ? 'disabled' : ''}
            >
            ${this.getCheckboxSVG()}
            <span class="facet-checkbox__text" aria-hidden="true">
              <span class="facet-checkbox__text-label">${rangeLabel}</span>
              (${group.totalCount})
            </span>
          </label>
      `;

      // Store item data as data attributes for the checkbox
      const itemsData = group.items.map(item => ({
        paramName: item.paramName,
        value: item.value
      }));
      
      // Add hidden inputs for each value in the range (will be managed by JavaScript)
      group.items.forEach(item => {
        html += `
          <input
            type="checkbox"
            name="${item.paramName}"
            value="${item.value}"
            class="height-range-hidden-input"
            data-group-index="${groupIndex}"
            ${item.checked ? 'checked' : ''}
            style="display: none;"
          >
        `;
      });

      html += `</li>`;
      itemIndex++;
    });

    return html;
  }

  getCaretSVG() {
    // Return a simple caret SVG
    return `<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>
    </svg>`;
  }

  getCheckboxSVG() {
    // Return checkbox SVGs (unchecked and checked)
    return `
      <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" class="facet-checkbox-svg" aria-hidden="true" focusable="false">
        <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1" class="facet-checkbox-rect"></rect>
      </svg>
      <div class="svg-wrapper">
        <svg class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    `;
  }

  attachEventListeners(filterList) {
    // Add event listeners for range checkboxes
    const rangeCheckboxes = filterList.querySelectorAll('.height-range-master-checkbox');
    
    rangeCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        const groupIndex = checkbox.dataset.groupIndex;
        const isChecked = checkbox.checked;
        
        // Find all hidden inputs for this group
        const hiddenInputs = filterList.querySelectorAll(`.height-range-hidden-input[data-group-index="${groupIndex}"]`);
        
        // Check/uncheck all hidden inputs
        hiddenInputs.forEach(input => {
          input.checked = isChecked;
        });
        
        // Update the visual state
        const label = checkbox.closest('.facets__label');
        if (isChecked) {
          label.classList.add('active');
        } else {
          label.classList.remove('active');
        }
        
        // Trigger the form's input event to update filters
        const form = checkbox.closest('form');
        if (form) {
          // Create a custom event that the facets.js can handle
          form.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    });

    // Set initial indeterminate state if needed
    rangeCheckboxes.forEach(checkbox => {
      const groupIndex = checkbox.dataset.groupIndex;
      const hiddenInputs = filterList.querySelectorAll(`.height-range-hidden-input[data-group-index="${groupIndex}"]`);
      const checkedCount = Array.from(hiddenInputs).filter(input => input.checked).length;
      
      if (checkedCount > 0 && checkedCount < hiddenInputs.length) {
        checkbox.indeterminate = true;
      }
    });
  }

  observeFacetUpdates() {
    // Watch for changes to the facets container
    const facetsContainer = document.querySelector('.facets-container');
    if (!facetsContainer) return;

    const observer = new MutationObserver((mutations) => {
      // Check if facets have been updated
      const facetsUpdated = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          return node.nodeType === 1 && (
            node.classList?.contains('facets__form') ||
            node.classList?.contains('facets__form-vertical') ||
            node.querySelector?.('.facets__list')
          );
        });
      });

      if (facetsUpdated) {
        // Re-apply grouping after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.groupHeightFilters();
          this.fixAllHeightLabels();
        }, 100);
      }
    });

    observer.observe(facetsContainer, {
      childList: true,
      subtree: true
    });
  }
  
  fixAllHeightLabels() {
    // Fix all instances of "Height_Text" in the DOM
    const elementsWithText = document.querySelectorAll('*:not(script):not(style)');
    elementsWithText.forEach(element => {
      // Fix text content
      if (element.childNodes.length === 1 && element.childNodes[0].nodeType === 3) {
        const text = element.textContent;
        if (text && text.includes('Height_Text')) {
          element.textContent = text.replace(/Height_Text/g, 'Height');
        }
      }
      
      // Fix aria-label attributes
      if (element.hasAttribute('aria-label')) {
        const ariaLabel = element.getAttribute('aria-label');
        if (ariaLabel && ariaLabel.includes('Height_Text')) {
          element.setAttribute('aria-label', ariaLabel.replace(/Height_Text/g, 'Height'));
        }
      }
      
      // Fix title attributes
      if (element.hasAttribute('title')) {
        const title = element.getAttribute('title');
        if (title && title.includes('Height_Text')) {
          element.setAttribute('title', title.replace(/Height_Text/g, 'Height'));
        }
      }
    });
    
    // Specifically target the mobile facets as well
    const mobileFacets = document.querySelectorAll('.mobile-facets__details');
    mobileFacets.forEach(facet => {
      if (facet.id && facet.id.includes('filter.p.m.custom.height_text')) {
        const summarySpan = facet.querySelector('summary .mobile-facets__label');
        if (summarySpan && summarySpan.textContent.includes('Height_Text')) {
          summarySpan.textContent = summarySpan.textContent.replace(/Height_Text/g, 'Height');
        }
      }
    });
  }
}

// Initialize the height filter grouping
if (typeof window !== 'undefined') {
  new HeightFilterGrouping();
}